import React from 'react';
import { restaurants, wellness, events, explore, meetings, gifts } from '../../data/mock';
import { SectionEyebrow, SectionTitle, SectionLead, GhostLink, Reveal } from './UI';
import { IMAGE_SIZES, responsiveImageProps } from '../../lib/images';

export const DiningSection = () => (
  <section id="dining" className="bg-[#0f1216] text-white">
    <div className="max-w-[1440px] mx-auto px-5 md:px-10 py-24 md:py-32">
      <div className="grid md:grid-cols-12 gap-6 md:gap-10 items-end">
        <div className="md:col-span-6">
          <Reveal>
            <div className="tracking-[0.32em] text-[10.5px] uppercase text-white/60">Dining</div>
            <h2 className="mt-6 font-serif text-[34px] md:text-[52px] leading-[1.05] tracking-[-0.01em]">Four kitchens.<br /> One long table on the strait.</h2>
          </Reveal>
        </div>
        <div className="md:col-span-5 md:col-start-8">
          <Reveal>
            <p className="text-white/70 text-[15px] md:text-[17px] leading-[1.75] font-light">
              A restaurant to open the day, a lounge to close it, and two destination dining rooms in between — each led by a chef with a distinct point of view.
            </p>
          </Reveal>
        </div>
      </div>

      <div className="mt-14 md:mt-20 grid md:grid-cols-2 gap-6 md:gap-10">
        {restaurants.map((r, idx) => (
          <Reveal key={r.id} className={idx % 2 === 1 ? 'md:mt-16' : ''}>
            <a href="#" className="group block">
              <div className="relative overflow-hidden aspect-[4/5]">
                <img
                  {...responsiveImageProps(r.image, IMAGE_SIZES.twoColumn)}
                  alt={r.name}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform [transition-duration:1600ms] ease-out"
                />
                <div className="absolute inset-0 bg-black/10 group-hover:bg-black/20 transition-colors" />
              </div>
              <div className="mt-6 flex items-start justify-between gap-6">
                <div>
                  <div className="tracking-[0.28em] text-[10.5px] uppercase text-white/60">{r.category}</div>
                  <div className="mt-3 font-serif text-2xl md:text-3xl leading-tight">{r.name}</div>
                  <p className="mt-3 text-white/70 font-light max-w-md">{r.desc}</p>
                </div>
                <div className="shrink-0 hidden md:block tracking-[0.22em] text-[10.5px] uppercase text-white/60 text-right">
                  {r.hours}
                </div>
              </div>
              <div className="mt-5 inline-flex items-center gap-3 tracking-[0.22em] text-[11px] uppercase text-white group-hover:text-white/70 transition-colors">
                <span className="inline-block w-7 h-px bg-current" />
                Discover More
              </div>
            </a>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const WellnessSection = () => (
  <section id="wellness" className="bg-white">
    <div className="grid md:grid-cols-2 items-stretch">
      <Reveal className="order-2 md:order-1">
        <div className="h-full flex items-center">
          <div className="max-w-xl mx-auto px-5 md:px-10 py-16 md:py-24">
            <SectionEyebrow>Spa & Wellness</SectionEyebrow>
            <SectionTitle className="mt-6">{wellness.title}</SectionTitle>
            <SectionLead className="mt-6">{wellness.desc}</SectionLead>
            <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-y-3 text-[14px] text-neutral-700">
              {wellness.points.map((p) => (
                <li key={p} className="flex items-start gap-3">
                  <span className="mt-[9px] w-1.5 h-1.5 bg-neutral-900 shrink-0" />
                  {p}
                </li>
              ))}
            </ul>
            <div className="mt-10"><GhostLink>Explore The Spa</GhostLink></div>
          </div>
        </div>
      </Reveal>
      <div className="order-1 md:order-2 relative overflow-hidden min-h-[420px] md:min-h-[720px]">
        <img
          {...responsiveImageProps(wellness.image, IMAGE_SIZES.half)}
          alt={wellness.title}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover"
        />
      </div>
    </div>
  </section>
);

export const CelebrationsSection = () => (
  <section id="celebrations" className="relative bg-black">
    <div className="relative w-full h-[70vh] md:h-[92vh] overflow-hidden">
      <img
        {...responsiveImageProps(events.image, IMAGE_SIZES.full)}
        alt="Celebrations"
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/25 to-black/40" />
      <div className="absolute inset-0 flex items-end">
        <div className="max-w-[1440px] mx-auto w-full px-5 md:px-10 pb-14 md:pb-24 text-white">
          <div className="max-w-2xl">
            <div className="tracking-[0.32em] text-[10.5px] uppercase text-white/80">Celebrations</div>
            <h2 className="mt-6 font-serif text-[34px] md:text-[56px] leading-[1.05] tracking-[-0.01em]">{events.title}</h2>
            <p className="mt-5 max-w-xl text-white/85 font-light text-[15px] md:text-[17px] leading-relaxed">{events.desc}</p>
            <div className="mt-8 inline-flex items-center gap-3 tracking-[0.22em] text-[11px] uppercase">
              <span className="inline-block w-8 h-px bg-white" />
              Plan Your Celebration
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export const ExploreSection = () => (
  <section id="explore" className="bg-[#f6f3ee]">
    <div className="max-w-[1440px] mx-auto px-5 md:px-10 py-24 md:py-32">
      <div className="grid md:grid-cols-12 gap-6 items-end">
        <div className="md:col-span-6">
          <Reveal>
            <SectionEyebrow>Explore Istanbul</SectionEyebrow>
            <SectionTitle className="mt-6">A city best entered slowly.</SectionTitle>
          </Reveal>
        </div>
        <div className="md:col-span-5 md:col-start-8">
          <Reveal>
            <SectionLead>
              Our concierge writes each itinerary by hand — a private cruise at dawn, a hammam at dusk, a table in a courtyard only locals know.
            </SectionLead>
          </Reveal>
        </div>
      </div>
      <div className="mt-12 md:mt-16 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5">
        {explore.map((x) => (
          <Reveal key={x.id}>
            <a href="#" className="group block">
              <div className="relative overflow-hidden aspect-[3/4]">
                <img
                  {...responsiveImageProps(x.image, IMAGE_SIZES.quarter)}
                  alt={x.title}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform [transition-duration:1400ms] ease-out"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/55 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 text-white font-serif text-lg md:text-xl leading-tight">{x.title}</div>
              </div>
            </a>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const MeetingsSection = () => (
  <section id="meetings" className="bg-white">
    <div className="grid md:grid-cols-2 items-stretch">
      <div className="relative overflow-hidden min-h-[420px] md:min-h-[680px]">
        <img
          {...responsiveImageProps(meetings.image, IMAGE_SIZES.half)}
          alt={meetings.title}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover"
        />
      </div>
      <Reveal>
        <div className="h-full flex items-center">
          <div className="max-w-xl mx-auto px-5 md:px-10 py-16 md:py-24">
            <SectionEyebrow>Meetings & Events</SectionEyebrow>
            <SectionTitle className="mt-6">{meetings.title}</SectionTitle>
            <SectionLead className="mt-6">{meetings.desc}</SectionLead>
            <div className="mt-10"><GhostLink>Request A Proposal</GhostLink></div>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

export const GiftsSection = () => (
  <section id="gifts" className="bg-white">
    <div className="max-w-[1440px] mx-auto px-5 md:px-10 py-24 md:py-32">
      <div className="grid md:grid-cols-12 gap-6 items-end">
        <div className="md:col-span-6">
          <Reveal>
            <SectionEyebrow>Gifts</SectionEyebrow>
            <SectionTitle className="mt-6">Give a night on the Bosphorus.</SectionTitle>
          </Reveal>
        </div>
        <div className="md:col-span-5 md:col-start-8">
          <Reveal>
            <SectionLead>Considered gift certificates for stays, rituals and tastings — delivered as a keepsake envelope.</SectionLead>
          </Reveal>
        </div>
      </div>
      <div className="mt-14 grid md:grid-cols-3 gap-6 md:gap-8">
        {gifts.map((g) => (
          <Reveal key={g.id}>
            <a href="#" className="group block">
              <div className="relative overflow-hidden aspect-[4/5]">
                <img
                  {...responsiveImageProps(g.image, IMAGE_SIZES.third)}
                  alt={g.title}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform [transition-duration:1400ms] ease-out"
                />
              </div>
              <div className="mt-5">
                <div className="font-serif text-xl md:text-2xl text-neutral-900">{g.title}</div>
                <p className="mt-2 text-neutral-600 font-light">{g.desc}</p>
                <div className="mt-4 inline-flex items-center gap-3 tracking-[0.22em] text-[11px] uppercase text-neutral-900">
                  <span className="inline-block w-7 h-px bg-current" />
                  Purchase
                </div>
              </div>
            </a>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
